import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import useCategorizationProgress from './useCategorizationProgress';
import { resolvePhase4Category } from '../utils/phase4CategoryOptions';

export const phase4SuggestionFor = (data, target, kind, wordIndex = null) => {
  if (!target) return null;
  const text = kind === 'coping_mechanism' ? target.identity.what_is_it || '' : target.identity.words?.[wordIndex];
  const matches = (data?.ai_suggestions || []).filter(s => s.item_index === target.item_index &&
    s.kind === kind && (kind !== 'thematic_domain' || s.word_index === wordIndex));
  if (matches.length !== 1 || matches[0].target_text !== text) return null;
  const suggestion = matches[0];
  if (suggestion.source_identity && JSON.stringify(suggestion.source_identity) !== JSON.stringify(target.identity)) return null;
  return suggestion;
};
export const phase4SuggestionValue = (suggestion, options) => {
  if (!suggestion || suggestion.ai_error || typeof suggestion.ai_suggestion !== 'string') return null;
  return resolvePhase4Category(suggestion.ai_suggestion, options);
};

// Only confirmed server reads update dropdowns and summaries. Automatic and manual
// requests may overlap; the server CAS preserves the clinician's correction.
export default function usePhase4Categories({ sessionId, API, data, editable, refresh, revision, copingOptions, domainOptions }) {
  const scope = useRef(null);
  if (scope.current?.id !== sessionId) scope.current = { id: sessionId, active: true, attempts: new Set(), mismatches: new Set(), queues: new Map(), autoQueue: Promise.resolve() };
  const current = scope.current;
  const live = () => scope.current === current && current.active;
  const refreshRef = useRef(refresh); refreshRef.current = refresh;
  const [state, setState] = useState({ pending: 0, error: '' });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    current.active = true; setState({ pending: 0, error: '' });
    return () => { current.active = false; };
  }, [current]);
  const reset = () => { current.attempts.clear(); current.halted = false; setState(s => ({ ...s, error: '' })); setRetry(n => n + 1); };
  const rows = (data?.selection_targets || []).flatMap(target => {
    const needed = (eligible, selected) => eligible === true || (eligible !== false && (selected == null || selected === ''));
    const row = (kind, wi, options) => {
      const suggestion = phase4SuggestionFor(data, target, kind, wi);
      return { suggestion, value: phase4SuggestionValue(suggestion, options) };
    };
    return [
      ...(needed(target.coping_auto_eligible, target.coping_mechanism) ? [row('coping_mechanism', null, copingOptions)] : []),
      ...target.word_indices.filter(wi => Number.isInteger(wi) && needed(target.domain_auto_eligible?.[wi], target.thematic_domains?.[wi]))
        .map(wi => row('thematic_domain', wi, domainOptions))
    ];
  });
  const progress = useCategorizationProgress({ phase: 4, sessionId, API, data, editable, rows,
    pending: state.pending, saveError: state.error, reset, refresh: () => refreshRef.current() });
  current.halted = Boolean(progress.error);
  const save = (displayItemIndex, kind, displayWordIndex, value, automatic = false) => {
    const target = data?.selection_targets?.[displayItemIndex];
    const wi = kind === 'thematic_domain' ? target?.word_indices?.[displayWordIndex] : null;
    if (!editable || !live() || data?.session_id !== sessionId || !target || (kind === 'thematic_domain' && !Number.isInteger(wi))) return;
    const key = `${target.item_index}:${kind}:${wi}`;
    const payload = { item_index: target.item_index, word_index: wi, kind, value,
      source_identity: target.identity, only_if_empty: automatic };
    revision.current += 1;
    setState(s => ({ ...s, pending: s.pending + 1, error: automatic ? s.error : '' }));
    const previous = automatic ? Promise.resolve() : current.queues.get(key) || Promise.resolve();
    const operation = previous.catch(() => {}).then(async () => {
      if (!live()) return;
      try {
        await axios.patch(`${API}/sessions/${sessionId}/phase4/select-category`, payload, { skipGlobalLoading: true, timeout: 30000 });
        if (live()) {
          revision.current += 1;
          const refreshed = await refreshRef.current();
          if (refreshed === false) throw new Error('Saved state could not be verified');
        }
      } catch (error) {
        current.halted = true;
        if (live()) setState(s => ({ ...s, error: 'Category save could not be confirmed. Retry AI selection or select a category manually.' }));
      } finally {
        if (live()) setState(s => ({ ...s, pending: s.pending - 1 }));
      }
    });
    if (!automatic) current.queues.set(key, operation);
    return operation;
  };
  useEffect(() => {
    if (!editable || current.halted || data?.session_id !== sessionId) return;
    (data.selection_targets || []).forEach((target, i) => {
      const consider = (kind, displayWordIndex, wi, selected, options, eligible) => {
        if (eligible === false || (eligible !== true && selected != null && selected !== '')) return;
        const suggestion = phase4SuggestionFor(data, target, kind, wi);
        const value = phase4SuggestionValue(suggestion, options);
        if (!value) {
          const mismatchKey = `${target.item_index}:${kind}:${wi}`;
          if (suggestion && !suggestion.ai_error && !current.mismatches.has(mismatchKey)) {
            current.mismatches.add(mismatchKey);
            console.warn(`Phase 4 ${kind}: AI suggestion has no unique configured option; automatic selection skipped.`);
          }
          return;
        }
        const row = `${target.item_index}:${kind}:${wi}`;
        const key = JSON.stringify([row, target.identity, suggestion]);
        if (current.attempts.has(key) || current.queues.has(row)) return;
        current.attempts.add(key);
        current.autoQueue = current.autoQueue.then(() => { if (live() && !current.halted) return save(i, kind, displayWordIndex, value, true); });
      };
      consider('coping_mechanism', null, null, target.coping_mechanism, copingOptions, target.coping_auto_eligible);
      if (target.thematic_domains != null && !Array.isArray(target.thematic_domains)) return;
      target.word_indices.forEach((wi, displayed) => {
        if (Number.isInteger(wi)) consider('thematic_domain', displayed, wi, target.thematic_domains?.[wi], domainOptions, target.domain_auto_eligible?.[wi]);
      });
    });
  }, [data, editable, sessionId, retry, copingOptions.join('\n'), domainOptions.join('\n')]);
  useEffect(() => {
    if (!editable || data?.session_id !== sessionId) return undefined;
    const empty = value => value == null || value === '';
    // Also observe regeneration after a stored invalid/error suggestion. These
    // are reads only; failed saves remain retryable without repeated PATCHes.
    const waiting = (data.selection_targets || []).some(target =>
      target.coping_auto_eligible === true || empty(target.coping_mechanism) ||
      target.word_indices.some(wi => Number.isInteger(wi) && (target.domain_auto_eligible?.[wi] === true || empty(target.thematic_domains?.[wi]))));
    if (!waiting) return undefined;
    const timer = setInterval(() => { if (live() && state.pending === 0) refreshRef.current(); }, 5000);
    return () => clearInterval(timer);
  }, [data, editable, sessionId, state.pending]);
  return { ...state, save, progress, retry: reset };
}
